import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authStore } from "../../../stores/auth.store";
import { useToast } from "../../../hooks/useToast";
import { useSignin } from "../hooks/useSignin";

/**
 * Landing page for social sign-in.
 *
 * The API finishes the OAuth handshake and redirects here with the user and
 * tokens as query parameters. This page turns them into a session — the same
 * one the password flow creates — and forwards the user on. Without it, signing
 * in with Google dead-ends on the Not Found page.
 */
const isAdminRole = (role) => role === "admin" || role === "superAdmin";

const parseParam = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(decodeURIComponent(value));
  } catch (err) {
    return null;
  }
};

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const { getAssistantOnboardingRedirect } = useSignin();

  const [failed, setFailed] = useState(false);
  // React runs effects twice in StrictMode; the session should only be
  // established once.
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const user = parseParam(searchParams.get("user"));
    const tokens = parseParam(searchParams.get("tokens"));
    const accessToken = tokens?.accessToken;

    if (!user || !accessToken) {
      setFailed(true);
      toast.error("We could not complete that sign-in. Please try again.");
      navigate("/auth/login", { replace: true });
      return;
    }

    const finish = async () => {
      const role = user.role;

      // Social sign-in has no "remember me" checkbox, so the session lives for
      // the browser session only, matching an unchecked password login.
      authStore.setToken(accessToken, false, role);

      let isProfileComplete = Boolean(
        user.profileCompleted || user.profileComplete || user.isProfileComplete
      );
      let assistantRedirect = null;

      if (role === "serviceProvider" || role === "assistant") {
        try {
          const onboarding = await getAssistantOnboardingRedirect();
          assistantRedirect = onboarding.redirectTo;
          isProfileComplete = onboarding.isComplete;
        } catch (err) {
          // Fall back to the base completion check below.
        }
      }

      authStore.setUser({ ...user, profileCompleted: isProfileComplete, rememberMe: false });

      const redirectTo = isAdminRole(role)
        ? "/admin"
        : role === "serviceProvider" || role === "assistant"
          ? assistantRedirect ||
            (isProfileComplete ? "/dashboards/assistant/overview" : "/assistant/kyc")
          : "/user/travel-assistant";

      navigate(redirectTo, { replace: true });
    };

    finish();
  }, [searchParams, navigate, toast, getAssistantOnboardingRedirect]);

  if (failed) return null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-4">
      <div className="text-center">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-[#61013B]/20 border-t-[#61013B]" />
        <p className="mt-4 text-base font-medium text-gray-700">Signing you in...</p>
      </div>
    </div>
  );
}
